import { Box, Button } from "@mui/material";
import { JSONContent } from "@tiptap/react";
import SimpleRichEditor, { Handle } from "components/editor/SimpleRichEditor";
import { RichContent } from "components/editor/utils";
import { Flag } from "components/engagement/flags";
import FlagsSelect from "components/engagement/notes/FlagsSelect";
import TypeSelect from "components/engagement/notes/TypeSelect";
import useCached from "components/hooks/useCached";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useCreateAdvisorAnnotation } from "services/admin";

export interface AddAnnotationRef {
  changeNoteType: (type: string) => void;
}

export default forwardRef<
  AddAnnotationRef,
  {
    advisorRef?: string;
    applicationRef?: string;
    noteTypes: string[];
    includeFlags?: boolean;
    placeholder?: string;
    onUpdate: () => Promise<void>;
    forceOpen?: boolean;
    flagOptions?: Flag[];
  }
>(
  (
    {
      advisorRef,
      applicationRef,
      onUpdate,
      noteTypes,
      includeFlags = true,
      placeholder = "Add family note",
      forceOpen = true,
      flagOptions,
    },
    ref,
  ) => {
    const editorRef = useRef<Handle>(null);
    const [content, setContent] = useCached<RichContent | null>(
      `admin-annotation-${advisorRef || applicationRef}`,
      null,
    );
    const [_initialContent] = useState<string | JSONContent | undefined>(
      (content?.text?.length && content?.json && JSON.parse(content?.json)) ||
        undefined,
    );
    const [noteType, setNoteType] = useState(noteTypes[0]);
    const [flags, setFlags] = useState<string[]>([]);
    const [annotationKey, setAnnotationKey] = useState("");
    const [uploadsInProgress, setUploadsInProgress] = useState(false);
    const { request, loading } = useCreateAdvisorAnnotation();

    useImperativeHandle(ref, () => ({
      changeNoteType: (type: string) => {
        setNoteType(type);
        editorRef.current?.focus();
      },
    }));

    const addAnnotation = async () => {
      if (!content) return;
      const resp = await request({
        advisorRef: advisorRef || "",
        applicationRef: applicationRef || "",
        contentType: "json",
        content: content.json,
        textContent: content.text,
        attachments: content.attachments,
        noteType: noteType,
        flags: flags,
      });
      if (!resp?.annotation) return;
      setContent(null);
      setAnnotationKey(resp.annotation.ref);
      setFlags([]);
      onUpdate();
    };

    useEffect(() => {
      setContent(null);
      setAnnotationKey(`admin-annotation-${advisorRef || applicationRef}`);
      setFlags([]);
    }, [advisorRef, applicationRef]);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <SimpleRichEditor
          forceOpen={forceOpen}
          ref={editorRef}
          key={annotationKey}
          disabled={loading}
          setContent={setContent}
          placeholder={placeholder}
          setUploadsInProgress={setUploadsInProgress}
          initialContent={_initialContent}
          primaryAction={
            <Button
              size="small"
              sx={{
                height: "unset",
                padding: "8px 18px",
              }}
              onClick={addAnnotation}
              disabled={loading || !content || uploadsInProgress}
            >
              Add
            </Button>
          }
          leftActionsPrefix={
            <>
              <TypeSelect
                defaultType={noteType}
                setType={setNoteType}
                typeOptions={noteTypes}
              />
              {includeFlags && (
                <FlagsSelect
                  flagOptions={flagOptions}
                  flags={flags}
                  setFlags={setFlags}
                />
              )}
            </>
          }
        />
      </Box>
    );
  },
);
